import { useCallback } from 'react';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { isSome } from 'payments-checkout-lib/types/utils/helpers';
import { getLineItemRecurringFrequencyBySubtotal } from 'payments-checkout-lib/utils/recurring';
import FormattedPrice from 'payments-checkout-lib/components/Utilities/FormattedPrice';
import { AT_CHECKOUT, FUTURE_START_DATE, RELATIVE_START_DATE } from 'payments-checkout-lib/constants/LineItems';
import { fromJS } from 'immutable';
import { useSelector } from 'react-redux';
import { getTaxCalculationId } from '../state/calculateTaxSlice';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
const RecurringSubscriptionSubtext = ({
  currencyCode,
  recurringSubtotal,
  shortenDescription = false,
  hasCheckoutFees = false
}) => {
  const {
    checkoutSession: session
  } = usePaymentFormContext();
  const hasCalculateAtCheckoutTaxes = session.get('isAutomatedSalesTaxEnabled');
  const taxCalculationId = useSelector(getTaxCalculationId);
  const total = recurringSubtotal && recurringSubtotal.total;
  const recurringBillingTerms = recurringSubtotal && fromJS(recurringSubtotal.recurringBillingTerms);
  const numPayments = recurringBillingTerms.get('numPayments');
  const initialPayment = recurringBillingTerms.get('initialPayment');
  const intervalUnit = recurringBillingTerms.get('intervalUnit');
  const intervalLength = recurringBillingTerms.get('intervalLength');
  const isAtCheckoutBillingStartDate = initialPayment.get('type') === AT_CHECKOUT;
  const isFixedBillingStartDate = initialPayment.get('type') === FUTURE_START_DATE;
  const isRelativeBillingStartDate = initialPayment.get('type') === RELATIVE_START_DATE;
  const relativeDelayLength = initialPayment.getIn(['relativeDelay', 'delayLength']);
  const relativeDelayUnit = initialPayment.getIn(['relativeDelay', 'delayUnit']);
  const numSubsequentPayments = !numPayments || isFixedBillingStartDate || isRelativeBillingStartDate ? numPayments : numPayments - 1;
  const futureStartDate = initialPayment.get('futureStartDate');
  const formattedDate = /*#__PURE__*/_jsx(FormattedDateTime, {
    value: Number(futureStartDate),
    format: "L",
    type: "utc"
  });
  const getMessageTranslationKey = useCallback(() => {
    if (isFixedBillingStartDate) {
      return isSome(numPayments) ? 'payments.recurringNumPaymentsWithStartDate_jsx' : 'payments.indefiniteRecurringPaymentWithStartDate_jsx';
    }
    if (isRelativeBillingStartDate) {
      return isSome(numPayments) ? 'payments.recurringNumPaymentsWithRelativeDate_jsx' : 'payments.indefiniteRecurringPaymentWithRelativeDate_jsx';
    }
    if (shortenDescription) {
      return isSome(numPayments) ? 'payments.recurringNumPaymentsShort_jsx' : 'payments.indefiniteRecurringPaymentsShort_jsx';
    }
    return isSome(numPayments) ? 'payments.recurringNumPayments_jsx' : 'payments.indefiniteRecurringPayments_jsx';
  }, [numPayments, isFixedBillingStartDate, isRelativeBillingStartDate, shortenDescription]);
  if (numSubsequentPayments === 0 && isAtCheckoutBillingStartDate) {
    return null;
  }
  return /*#__PURE__*/_jsx(Flex, {
    "data-test-id": "recurring-subscription-subtext",
    justifyContent: "end",
    marginBlockEnd: "50",
    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: getMessageTranslationKey(),
      options: Object.assign({
        priceProps: Object.assign({
          currencyCode,
          price: total,
          frequency: getLineItemRecurringFrequencyBySubtotal(intervalUnit, intervalLength),
          simplify: shortenDescription,
          plusTax: hasCalculateAtCheckoutTaxes && !taxCalculationId,
          includesFees: hasCheckoutFees
        }, !shortenDescription && {
          currencyAndAmountStyle: {
            typography: 'heading-25',
            color: 'text-core-subtle'
          }
        })
      }, isRelativeBillingStartDate && {
        delayLength: relativeDelayLength,
        delayUnit: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `payments.relativeStartDateDelayUnits.${relativeDelayUnit}`,
          options: {
            count: relativeDelayLength
          }
        })
      }, {
        count: numSubsequentPayments,
        formattedDate
      }),
      elements: {
        FormattedPrice
      }
    })
  });
};
export default RecurringSubscriptionSubtext;